import { OVERLAY_CLASSNAMES } from "@/components/common/FileUploadZone/FileUploadZone"
import { useDragDropOverlay } from "@/hooks/useDragDropOverlay"
import { cn } from "@/lib/utils"
import { IconUpload } from "@tabler/icons-react"
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react"

interface DropOverlayProps {
  /** File extensions to accept, e.g. [".csv", ".xlsx"] */
  acceptedExtensions: string[]
  hint: string
  onFilesDropped: (files: File[]) => void
  /** Called with files that did not match acceptedExtensions */
  onRejectedFiles?: (files: File[]) => void
  /**
   * Opt-in: take the WHOLE drop in one call and do your own filtering. When set,
   * it replaces the accepted/rejected split entirely (the two callbacks above are
   * not fired), because a caller that reports per-file outcomes needs the drop as
   * one list — receiving it as two calls means the second overwrites the first's
   * state, and an all-rejected drop never arrives at all.
   */
  onAllFilesDropped?: (files: File[]) => void
  /**
   * Where the overlay paints. `viewport` (default) covers the whole window;
   * `container` covers only the visible box of the nearest SCROLLING ancestor
   * (overflow-y auto or scroll; the document if none), measured and pinned as
   * a fixed element, for pages whose drop target is the module body and NOT the
   * app chrome around it (menu bar, sidebar). The drag detection itself stays
   * window-wide either way.
   */
  scope?: "viewport" | "container"
  /**
   * Opt-in: when a dropped item is a folder, walk it (via
   * `DataTransferItem.webkitGetAsEntry()`) and include every file found
   * inside, flattened, alongside plain dropped files — instead of the
   * browser's default of contributing nothing for a folder entry. Hidden
   * entries (name starting with ".", e.g. `.DS_Store`, `.git`) are skipped
   * at every depth. Off by default: every other consumer of this overlay
   * keeps today's file-only behavior unchanged.
   */
  expandDirectories?: boolean
}

function isHiddenEntryName(name: string): boolean {
  return name.startsWith(".")
}

function readDirectoryEntries(
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  // `readEntries` returns AT MOST ~100 entries per call by spec — it must be
  // called repeatedly until it resolves an empty batch to see the rest.
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = []
    const readNext = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all)
          return
        }
        all.push(...batch)
        readNext()
      }, reject)
    }
    readNext()
  })
}

function readEntryAsFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

async function collectFilesFromEntry(entry: FileSystemEntry): Promise<File[]> {
  if (isHiddenEntryName(entry.name)) return []
  if (entry.isFile) {
    return [await readEntryAsFile(entry as FileSystemFileEntry)]
  }
  if (entry.isDirectory) {
    const children = await readDirectoryEntries(
      (entry as FileSystemDirectoryEntry).createReader(),
    )
    const nested = await Promise.all(children.map(collectFilesFromEntry))
    return nested.flat()
  }
  return []
}

/** Every top-level dropped item that resolves to a `FileSystemEntry`, or
 * `null` when NONE do — no item supports `webkitGetAsEntry` (unsupported
 * browser), or the drop carried no file/directory items at all — in which
 * case the caller falls back to `dataTransfer.files`. A mixed drop (e.g. a
 * file alongside a `text/uri-list` item from another app) still returns the
 * entries that DO resolve; a non-file item resolving to `null` must not
 * throw away the files that landed alongside it. */
function getTopLevelEntries(
  items: DataTransferItemList,
): FileSystemEntry[] | null {
  const entries = Array.from(items)
    .map((item) => item.webkitGetAsEntry?.())
    .filter((entry): entry is FileSystemEntry => !!entry)
  return entries.length > 0 ? entries : null
}

export function DropOverlay({
  acceptedExtensions,
  hint,
  onFilesDropped,
  onRejectedFiles,
  onAllFilesDropped,
  scope = "viewport",
  expandDirectories = false,
}: DropOverlayProps) {
  const { isDragActive, dismiss } = useDragDropOverlay()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [containerStyle, setContainerStyle] = useState<CSSProperties>()

  // Container scope: pin the overlay to the nearest SCROLLING ancestor's
  // visible box (the module's content region: right of the sidebar, below
  // the top bar), as a fixed element with measured edges. `absolute inset-0`
  // on a long page would follow the page height instead of the window, so
  // the hint could sit off-screen; measuring the scroll container gives the
  // visible region and re-measures on resize.
  useLayoutEffect(() => {
    if (scope !== "container" || !isDragActive) return undefined
    const measure = () => {
      let el: HTMLElement | null = anchorRef.current?.parentElement ?? null
      while (el && el !== document.body) {
        const ov = getComputedStyle(el).overflowY
        if (ov === "auto" || ov === "scroll") break
        el = el.parentElement
      }
      const r = (el ?? document.documentElement).getBoundingClientRect()
      setContainerStyle({
        position: "fixed",
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      })
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [scope, isDragActive])

  if (!isDragActive) {
    // Zero-size anchor so the measurement above can find the ancestor.
    return scope === "container" ? (
      <div ref={anchorRef} className="hidden" aria-hidden="true" />
    ) : null
  }

  const extSet = new Set(acceptedExtensions.map((e) => e.toLowerCase()))

  function getExt(file: File) {
    return "." + (file.name.split(".").pop()?.toLowerCase() ?? "")
  }

  return (
    <div
      ref={anchorRef}
      data-testid="drop-overlay"
      style={scope === "container" ? containerStyle : undefined}
      className={cn(
        "z-50 flex items-center justify-center",
        // Viewport scope covers the window; container scope gets its box
        // from the measurement above (same dashed inset-4 frame either way,
        // exactly like the import page).
        scope === "container" ? "fixed" : "fixed inset-0",
      )}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        dismiss()

        const dispatch = (allFiles: File[]) => {
          if (onAllFilesDropped) {
            if (allFiles.length) onAllFilesDropped(allFiles)
            return
          }
          const accepted = allFiles.filter((f) => extSet.has(getExt(f)))
          const rejected = allFiles.filter((f) => !extSet.has(getExt(f)))
          if (rejected.length) onRejectedFiles?.(rejected)
          if (accepted.length) onFilesDropped(accepted)
        }

        // `webkitGetAsEntry()` must be called synchronously, while the
        // native drop event is still live — capture every top-level entry
        // now, then walk them asynchronously.
        const topLevelEntries = expandDirectories
          ? getTopLevelEntries(e.dataTransfer.items)
          : null

        if (topLevelEntries) {
          void Promise.all(topLevelEntries.map(collectFilesFromEntry)).then(
            (nested) => dispatch(nested.flat()),
          )
          return
        }

        dispatch(Array.from(e.dataTransfer.files))
      }}
    >
      {/* tw-animate-css entrance: the dashed card fades and scales in
          instead of popping, and the icon lifts once so the drop target
          reads as "alive" while the file hovers. */}
      <div
        className={cn(
          OVERLAY_CLASSNAMES,
          "animate-in fade-in-0 zoom-in-95 duration-200 ease-out",
        )}
      >
        <IconUpload
          size={36}
          className="animate-in slide-in-from-bottom-2 text-primary duration-300"
        />
        <span className="text-base font-medium text-primary">{hint}</span>
      </div>
    </div>
  )
}
